import React, { useState } from 'react';
import {
  Modal,
  Button,
  List,
  Card,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { Route, Document } from '../../types';
import {
  useGetDocumentsByRouteQuery,
  useDeleteDocumentMutation
} from '../../store/api/documentsApi';
import UploadDocumentModal from '../documents/UploadDocumentModal';
import DocumentAuthImage from '../documents/DocumentAuthImage';
import { documentTypeLabel } from '../../utils/enumUiLabels';
import { resolveDocumentDisplayTypeLabel } from '../../utils/documentDisplayName';
import {
  downloadDocument,
  fetchDocumentObjectUrl,
  resolveDocumentFilename,
  documentPreviewEndpoint,
  viewDocument,
} from '../../utils/documentDownload';

const { Text, Title } = Typography;

interface RouteDocumentsModalProps {
  visible: boolean;
  route: Route | null;
  onClose: () => void;
  onChanged?: () => void;
}

const RouteDocumentsModal: React.FC<RouteDocumentsModalProps> = ({
  visible,
  route,
  onClose,
  onChanged
}) => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const { data: documents = [], refetch: refetchDocuments } = useGetDocumentsByRouteQuery(
    route?.id || 0,
    { skip: !route?.id }
  );
  const [deleteDocument] = useDeleteDocumentMutation();

  const handleUploadSuccess = () => {
    refetchDocuments();
    onChanged?.();
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await deleteDocument(documentId).unwrap();
      message.success('Документ успешно удален');
      refetchDocuments();
      onChanged?.();
    } catch (error: any) {
      console.error('Delete error:', error);
      const errorMessage = error?.data?.error || error?.message || 'Ошибка при удалении документа';
      message.error(errorMessage);
    }
  };

  const normalizeDocType = (t?: string | null) => String(t || '').toUpperCase();
  const clientDocs = documents.filter(doc => doc.fileType === 'file' && normalizeDocType(doc.documentType) === 'INVOICE');
  const carrierDocs = documents.filter(doc => doc.fileType === 'file' && ['CARRIER_INVOICE', 'LOADING_INVOICE'].includes(normalizeDocType(doc.documentType)));
  const otherFiles = documents.filter(doc => doc.fileType === 'file' && !['INVOICE', 'CARRIER_INVOICE', 'LOADING_INVOICE'].includes(normalizeDocType(doc.documentType)));
  const photos = documents.filter(doc => doc.fileType === 'photo');

  const handleViewDocument = async (document: Document) => {
    try {
      const displayName = resolveDocumentFilename(document);

      if (document.fileType === 'photo') {
        const { url, revoke } = await fetchDocumentObjectUrl(document.id, documentPreviewEndpoint(document.fileType));
        Modal.info({
          title: `Просмотр: ${displayName}`,
          content: (
            <div style={{ textAlign: 'center' }}>
              <img
                src={url}
                alt={displayName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            </div>
          ),
          width: 800,
          okText: 'Закрыть',
          afterClose: revoke,
        });
      } else {
        await viewDocument(document.id);
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      message.error('Ошибка при загрузке файла');
    }
  };

  const handleDownloadDocument = async (document: Document) => {
    try {
      await downloadDocument(document.id, document);
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      message.error('Не удалось скачать файл');
    }
  };

  const renderFileList = (title: string, data: Document[]) => (
    <div style={{ marginBottom: '24px' }}>
      <Title level={4}>{title} ({data.length})</Title>
      <List
        grid={{ gutter: 16, column: 2 }}
        dataSource={data}
        renderItem={(document: Document) => (
          <List.Item>
            <Card
              size="small"
              actions={[
                <Button type="link" icon={<EyeOutlined />} onClick={() => void handleViewDocument(document)}>Просмотр</Button>,
                <Button type="link" icon={<DownloadOutlined />} onClick={() => void handleDownloadDocument(document)}>Скачать</Button>,
                <Popconfirm title="Удалить документ?" onConfirm={() => handleDeleteDocument(document.id)} okText="Да" cancelText="Нет">
                  <Button type="link" danger icon={<DeleteOutlined />}>Удалить</Button>
                </Popconfirm>
              ]}
            >
              <Card.Meta
                avatar={<FileTextOutlined style={{ fontSize: '24px' }} />}
                title={resolveDocumentFilename(document)}
                description={
                  <Space direction="vertical" size="small">
                    <div>
                      <Tag color="blue">{document.stage}</Tag>
                      {document.documentType && (
                        <Tag color="purple">{resolveDocumentDisplayTypeLabel(document, documentTypeLabel)}</Tag>
                      )}
                      {document.comment && <Text type="secondary">{document.comment}</Text>}
                    </div>
                    <Text type="secondary">{new Date(document.createdAt).toLocaleDateString('ru-RU')}</Text>
                  </Space>
                }
              />
            </Card>
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Modal
      title={`Документы маршрута: ${route?.number || `ID: ${route?.id}`}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnHidden
    >
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setUploadModalVisible(true)}
          disabled={!route}
        >
          Загрузить документ
        </Button>
      </div>

      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onSuccess={handleUploadSuccess}
        route={route || undefined}
      />

      {clientDocs.length > 0 && renderFileList('Клиентские документы', clientDocs)}
      {carrierDocs.length > 0 && renderFileList('Документы перевозчика', carrierDocs)}
      {otherFiles.length > 0 && renderFileList('Прочие документы', otherFiles)}

      <div>
        <Title level={4}>Фотографии ({photos.length})</Title>
        <List
          grid={{ gutter: 16, column: 3 }}
          dataSource={photos}
          renderItem={(document: Document) => (
            <List.Item>
              <Card
                size="small"
                cover={
                  <DocumentAuthImage
                    documentId={document.id}
                    fileType={document.fileType}
                    alt={resolveDocumentFilename(document)}
                  />
                }
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => void handleViewDocument(document)}
                  >
                    Просмотр
                  </Button>,
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => void handleDownloadDocument(document)}
                  >
                    Скачать
                  </Button>,
                  <Popconfirm
                    title="Удалить фотографию?"
                    onConfirm={() => handleDeleteDocument(document.id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Удалить
                    </Button>
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={resolveDocumentFilename(document)}
                  description={
                    <Space direction="vertical" size="small">
                      <div>
                        <Tag color="green">{document.stage}</Tag>
                        {document.documentType && (
                          <Tag color="purple">{resolveDocumentDisplayTypeLabel(document, documentTypeLabel)}</Tag>
                        )}
                        {document.comment && <Text type="secondary">{document.comment}</Text>}
                      </div>
                      <Text type="secondary">
                        {new Date(document.createdAt).toLocaleDateString('ru-RU')}
                      </Text>
                    </Space>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default RouteDocumentsModal;
